import React, { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { motion, AnimatePresence } from "motion/react"
import { Movie } from "../../types/entities/movie"
import { useRequest } from "../../hooks/useRequest"
import { appStore } from "../../store/appStore"
import { AfishaBanner } from "../../types/entities/banner"
import { CinemaAction } from "../../types/entities/action"
import { normalizeAfishaBanners } from "../../lib/afishaBanners"
import { normalizeCinemaActions } from "../../lib/afishaActions"
import { AfishaBannerSlider, ActionsTab } from "../../features/afisha"
import { HomeLoader, MovieItem } from "../../features/home"
import StartPopup from "../../components/StartPopup"
import ConfirmModal, { ConfirmModalHandle } from "../../components/ConfirmModal"
import PageTitle from "../../components/PageTitle"
import { FilmCollectionGroup } from "../../types/entities/collections"
import { AFISHA_TAB_TO_SLUG, afishaTabFromSlug, type AfishaTab } from "../../lib/afishaTabs"

const MONO = "'JetBrains Mono', monospace"
const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"

/* ─── Privilege-based collections ────────────────────────────────────────── */
type CollectionDef = {
    key: keyof Movie["privileges"]
    title: string
    description: string
    emoji: string
    color: string
}

const COLLECTIONS: CollectionDef[] = [
    {
        key: "invitational",
        title: "Фильмы по пригласительным",
        description: "Кино бесплатно с пригласительным билетом",
        emoji: "🎟",
        color: "#f5a623",
    },
    {
        key: "pushkin_card",
        title: "Кино по Пушкинской карте",
        description: "Фильмы, рекомендованные Министерством культуры РФ",
        emoji: "🎫",
        color: "#29cc6a",
    },
    {
        key: "discount",
        title: "Ого, скидка!",
        description: "Смотрите выбранные фильмы по сниженной цене",
        emoji: "💸",
        color: "#e4206b",
    },
]

const RU_MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"]
const DAY_NAMES_SHORT = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"]

/* ─── helpers ─────────────────────────────────────────────────────────────── */
/** Локальный YYYY-MM-DD — не toISOString(): в UTC+ он сдвигает день назад */
function toISO(date: Date): string {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
}

function parseISODate(iso: string): Date {
    const [y, m, d] = iso.split("-").map(Number)
    return new Date(y, m - 1, d)
}

function formatShort(iso: string): string {
    const [, m, d] = iso.split("-")
    return `${d}.${m}`
}

function addDaysISO(iso: string, days: number): string {
    const [y, m, d] = iso.split("-").map(Number)
    return toISO(new Date(y, m - 1, d + days))
}

/* ─── CalendarModal ────────────────────────────────────────────────────────── */
function CalendarModal({
    dates,
    selectedDate,
    onSelect,
    onClose,
}: {
    dates: string[]
    selectedDate: string
    onSelect: (d: string) => void
    onClose: () => void
}) {
    const availableSet = new Set(dates)

    const initialMonth = (() => {
        const d = selectedDate ? parseISODate(selectedDate) : dates[0] ? parseISODate(dates[0]) : new Date()
        return { year: d.getFullYear(), month: d.getMonth() }
    })()

    const allMonths = Array.from(
        new Set(dates.map(d => {
            const dt = parseISODate(d)
            return `${dt.getFullYear()}-${dt.getMonth()}`
        }))
    ).map(s => { const [y, m] = s.split("-"); return { year: Number(y), month: Number(m) } })

    const [idx, setIdx] = useState(() => {
        const i = allMonths.findIndex(m => m.year === initialMonth.year && m.month === initialMonth.month)
        return Math.max(i, 0)
    })

    const { year, month } = allMonths[idx] || initialMonth

    const days: (Date | null)[] = []
    const first = new Date(year, month, 1)
    const firstDow = (first.getDay() + 6) % 7 // Mon = 0
    for (let i = 0; i < firstDow; i++) days.push(null)
    const last = new Date(year, month + 1, 0).getDate()
    for (let d = 1; d <= last; d++) days.push(new Date(year, month, d))
    while (days.length % 7 !== 0) days.push(null)

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ background: "rgba(10,16,40,0.85)", backdropFilter: "blur(12px)" }}
            onClick={onClose}>
            <motion.div
                initial={{ scale: 0.94, y: 16 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.94, y: 16 }}
                transition={{ type: "spring", stiffness: 320, damping: 28 }}
                className="w-full max-w-[360px] rounded-[28px] border border-white/10 p-6 flex flex-col gap-4"
                style={{ background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)" }}
                onClick={e => e.stopPropagation()}>

                {/* Month navigation */}
                <div className="flex items-center justify-between">
                    <button
                        disabled={idx === 0}
                        onClick={() => setIdx(i => i - 1)}
                        className="size-9 flex items-center justify-center rounded-full bg-white/[0.07] disabled:opacity-30 hover:bg-white/[0.14] transition-colors"
                        style={{ fontFamily: MONO }}>
                        ◀
                    </button>
                    <span className="text-white text-[15px] capitalize" style={{ fontFamily: MONO, fontWeight: 700 }}>
                        {RU_MONTHS[month]} {year}
                    </span>
                    <button
                        disabled={idx === allMonths.length - 1}
                        onClick={() => setIdx(i => i + 1)}
                        className="size-9 flex items-center justify-center rounded-full bg-white/[0.07] disabled:opacity-30 hover:bg-white/[0.14] transition-colors"
                        style={{ fontFamily: MONO }}>
                        ▶
                    </button>
                </div>

                {/* Day-of-week header */}
                <div className="grid grid-cols-7 gap-1">
                    {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map(d => (
                        <div key={d} className="text-center text-[11px] text-white/30 py-1" style={{ fontFamily: MONO }}>{d}</div>
                    ))}
                </div>

                {/* Days grid */}
                <div className="grid grid-cols-7 gap-1">
                    {days.map((d, i) => {
                        if (!d) return <div key={i} />
                        const iso = toISO(d)
                        const avail = availableSet.has(iso)
                        const selected = iso === selectedDate
                        const today = iso === toISO(new Date())
                        return (
                            <button
                                key={iso}
                                disabled={!avail}
                                onClick={() => { onSelect(iso); onClose() }}
                                className="aspect-square flex items-center justify-center rounded-[10px] text-[14px] transition-all"
                                style={{
                                    fontFamily: MONO,
                                    fontWeight: selected ? 700 : today ? 600 : 400,
                                    background: selected ? "#349be6" : today ? "rgba(52,155,230,0.18)" : avail ? "rgba(255,255,255,0.06)" : "transparent",
                                    boxShadow: selected ? "0 0 14px rgba(52,155,230,0.5)" : "none",
                                    color: avail ? "#fff" : "rgba(255,255,255,0.15)",
                                    cursor: avail ? "pointer" : "default",
                                }}>
                                {d.getDate()}
                            </button>
                        )
                    })}
                </div>
            </motion.div>
        </motion.div>
    )
}

/* ─── DatePicker ──────────────────────────────────────────────────────────── */
const DATE_CHIPS_AVAILABLE = 7
const DATE_CHIPS_TOTAL_MAX = 10

/* Непрерывный ряд дней от первой даты: дни без расписания — серые заглушки.
   Ряд обрывается, когда набрали 7 доступных дат или 10 чипов всего —
   дальние даты (после длинного разрыва) остаются доступными через календарь */
function buildDateChips(dates: string[]): { iso: string; available: boolean }[] {
    if (dates.length === 0) return []
    const availableSet = new Set(dates)
    const chips: { iso: string; available: boolean }[] = []
    let iso = dates[0]
    let availableCount = 0

    while (chips.length < DATE_CHIPS_TOTAL_MAX && availableCount < DATE_CHIPS_AVAILABLE) {
        const available = availableSet.has(iso)
        // Не заканчиваем ряд серым «хвостом»: заглушки добавляем, только если
        // до конца ряда успеет попасть следующая доступная дата
        if (available) {
            chips.push({ iso, available })
            availableCount++
        } else {
            const next = dates.find(d => d > iso)
            if (!next) break
            const gapEnd = chips.length + (Date.parse(next) - Date.parse(iso)) / 86_400_000
            if (gapEnd >= DATE_CHIPS_TOTAL_MAX) break
            chips.push({ iso, available })
        }
        iso = addDaysISO(iso, 1)
    }
    return chips
}

function DatePicker({
    dates,
    selectedDate,
    onSelect,
    onUnavailableSelect,
    onCalendarOpen,
    onSoonSelect,
    soonActive,
}: {
    dates: string[]
    selectedDate: string
    onSelect: (d: string) => void
    onUnavailableSelect: () => void
    onCalendarOpen: () => void
    onSoonSelect: () => void
    soonActive: boolean
}) {
    const chips = buildDateChips(dates)
    const isOutside = selectedDate && !chips.some(c => c.available && c.iso === selectedDate)

    return (
        <div className="-mx-4 md:mx-0">
            <div className="overflow-x-auto py-5 -my-3 scrollbar-hide md:overflow-visible md:py-0 md:my-0">
                <div className="flex flex-nowrap gap-2.5 px-4 md:flex-wrap md:px-0">
                    {/* Calendar button — shows selected date if it's outside chips range */}
                    <motion.button
                        whileHover={{ scale: 1.04, y: -1 }}
                        whileTap={{ scale: 0.93 }}
                        onClick={onCalendarOpen}
                        className="flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-[12px] border border-white/15 px-3"
                        style={{
                            background: isOutside ? "#349be6" : "rgba(255,255,255,0.06)",
                            boxShadow: isOutside ? "0 0 14px rgba(52,155,230,0.4)" : "none",
                            fontFamily: MONO,
                        }}>
                        <span style={{ fontSize: 16 }}>📅</span>
                        {isOutside && (
                            <span className="text-[12px] text-white" style={{ fontFamily: MONO, fontWeight: 700 }}>
                                {formatShort(selectedDate)}
                            </span>
                        )}
                    </motion.button>

                    {chips.map(({ iso, available }, i) => {
                        const on = !soonActive && available && iso === selectedDate
                        const label = i === 0 ? "Сегодня" : i === 1 ? "Завтра" : DAY_NAMES_SHORT[parseISODate(iso).getDay()]
                        const sub = formatShort(iso)
                        return (
                            <motion.button
                                key={iso}
                                whileHover={available ? { scale: 1.04, y: -1 } : undefined}
                                whileTap={{ scale: 0.93 }}
                                onClick={() => (available ? onSelect(iso) : onUnavailableSelect())}
                                aria-disabled={!available}
                                className={`flex h-11 shrink-0 flex-col items-center justify-center rounded-[12px] px-4 ${available ? "" : "cursor-default"}`}
                                style={{
                                    background: on ? "#349be6" : available ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)",
                                    boxShadow: on ? "0 0 18px rgba(52,155,230,0.4)" : "none",
                                    border: available ? "none" : "1px dashed rgba(255,255,255,0.12)",
                                    minWidth: 64,
                                }}>
                                <span
                                    className={`text-[12px] ${available ? "text-white" : "text-white/30"}`}
                                    style={{ fontFamily: MONO, fontWeight: on ? 700 : 500, lineHeight: 1.1 }}>
                                    {label}
                                </span>
                                <span
                                    className={`text-[10px] ${available ? "text-white/60" : "text-white/20"}`}
                                    style={{ fontFamily: MONO }}>
                                    {sub}
                                </span>
                            </motion.button>
                        )
                    })}
                    <motion.button
                        whileHover={{ scale: 1.04, y: -1 }}
                        whileTap={{ scale: 0.93 }}
                        onClick={onSoonSelect}
                        aria-pressed={soonActive}
                        className="flex h-11 shrink-0 items-center justify-center rounded-[12px] border border-[#e4206b]/45 px-4 text-[12px] text-white"
                        style={{
                            background: soonActive ? "#e4206b" : "rgba(228,32,107,0.16)",
                            boxShadow: soonActive ? "0 0 18px rgba(228,32,107,0.45)" : "0 0 14px rgba(228,32,107,0.18)",
                            fontFamily: MONO,
                            fontWeight: 700,
                        }}>
                        Скоро
                    </motion.button>
                </div>
            </div>
        </div>
    )
}

/* ─── EmptyTab ────────────────────────────────────────────────────────────── */
function EmptyTab({ tab }: { tab: string }) {
    const icon = tab === "Акции" ? "🎟" : tab === "Новости" ? "📰" : "✦"
    return (
        <motion.div
            key={tab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center justify-center gap-4 py-24 text-white/30">
            <span className="text-[64px]">{icon}</span>
            <p className="text-center" style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 20 }}>{tab} скоро появятся</p>
            <p className="text-[14px] text-center text-white/40">Следите за обновлениями</p>
        </motion.div>
    )
}

function ScheduleSoonPlaceholder() {
    return (
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-white/30">
            <span className="text-[64px]">🎟</span>
            <p className="text-center" style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 20 }}>Расписание скоро дополнится</p>
            <p className="text-[14px] text-center text-white/40">Следите за обновлениями</p>
        </div>
    )
}

/* ─── Подборки (Collections) ──────────────────────────────────────────────── */
function filmCountLabel(n: number): string {
    if (n === 1) return "1 фильм"
    if (n >= 2 && n <= 4) return `${n} фильма`
    return `${n} фильмов`
}

function CollectionsTab({ groups }: { groups: FilmCollectionGroup[] }) {
    const [selected, setSelected] = useState<CollectionDef["key"] | null>(null)

    const collections = COLLECTIONS.map(def => {
        const fromApi = groups.find(g => g.key === def.key)
        return {
            ...def,
            items: fromApi?.films ?? [],
        }
    })

    const active = selected ? collections.find(c => c.key === selected) : null

    if (active) {
        return (
            <motion.div
                key={`detail-${active.key}`}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.22 }}
                className="flex flex-col gap-5 px-4 pt-2">
                <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-[12px] text-white/70 transition-colors hover:bg-white/[0.1] hover:text-white"
                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                    ← Все подборки
                </button>

                <div className="flex items-start gap-3">
                    <span className="text-[32px] shrink-0">{active.emoji}</span>
                    <div className="min-w-0">
                        <h3 className="text-white leading-tight" style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 20 }}>
                            {active.title}
                        </h3>
                        <p className="mt-1.5 text-[13px] leading-snug text-white/55" style={{ fontFamily: MONO }}>
                            {active.description}
                        </p>
                        <p className="mt-2 text-[12px]" style={{ fontFamily: MONO, color: active.color }}>
                            {filmCountLabel(active.items.length)}
                        </p>
                    </div>
                </div>

                {active.items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-white/30">
                        <span className="text-5xl">🎬</span>
                        <p style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 16 }}>Пока пусто</p>
                        <p className="text-[13px] text-center text-white/40">В этой подборке пока нет фильмов</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-5">
                        {active.items.map((movie, i) => (
                            <MovieItem key={movie.id} item={movie} index={i} showSessionDates />
                        ))}
                    </div>
                )}
            </motion.div>
        )
    }

    const hasAny = collections.some(c => c.items.length > 0)
    const sortedCollections = [...collections].sort((a, b) => b.items.length - a.items.length)

    return (
        <motion.div
            key="collections-grid"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="px-4 pt-2">
            {!hasAny && (
                <p className="mb-4 text-center text-[13px] text-white/40" style={{ fontFamily: MONO }}>
                    Подборки пока пустые
                </p>
            )}

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {sortedCollections.map((col, i) => {
                    const empty = col.items.length === 0
                    const count = col.items.length
                    const countSize = count >= 10 ? "text-[56px]" : "text-[88px]"
                    const accent = empty ? "rgba(255,255,255,0.18)" : col.color
                    return (
                        <motion.button
                            key={col.key}
                            type="button"
                            disabled={empty}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: empty ? 0.55 : 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            whileHover={empty ? undefined : { y: -8, rotate: i % 2 ? 1.5 : -1.5 }}
                            whileTap={empty ? undefined : { scale: 0.98 }}
                            onClick={() => setSelected(col.key)}
                            className={[
                                "relative overflow-hidden rounded-[26px] border p-6 text-left disabled:cursor-not-allowed",
                                empty
                                    ? "border-white/[0.06] bg-white/[0.02] saturate-0"
                                    : "border-white/10 bg-white/[0.04]",
                            ].join(" ")}>
                            <span
                                aria-hidden
                                className={`absolute -right-2 -top-4 ${countSize} leading-none select-none ${empty ? "opacity-[0.04]" : "opacity-[0.07]"}`}
                                style={{ fontFamily: DISPLAY, fontWeight: 900, color: accent }}>
                                {count}
                            </span>

                            {empty && (
                                <span
                                    className="absolute right-4 top-4 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] text-white/35"
                                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                                    недоступно
                                </span>
                            )}

                            <div className={`mb-4 text-[52px] leading-none ${empty ? "opacity-40" : ""}`}>{col.emoji}</div>
                            <h3
                                className={`mb-2 ${empty ? "text-white/45" : "text-white"}`}
                                style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 18 }}>
                                {col.title}
                            </h3>
                            <p
                                style={{
                                    fontFamily: BODY,
                                    fontWeight: 500,
                                    fontSize: 14,
                                    color: empty ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.55)",
                                    lineHeight: 1.6,
                                }}>
                                {col.description}
                            </p>
                            <div className="mt-4 flex items-center gap-3">
                                <div className="h-1.5 w-10 shrink-0 rounded-full" style={{ background: accent }} />
                                <span
                                    className="text-[12px]"
                                    style={{
                                        fontFamily: MONO,
                                        fontWeight: 700,
                                        color: empty ? "rgba(255,255,255,0.3)" : col.color,
                                    }}>
                                    {empty ? "нет фильмов" : filmCountLabel(col.items.length)}
                                </span>
                            </div>
                        </motion.button>
                    )
                })}
            </div>
        </motion.div>
    )
}

/* ─── Page header strip ───────────────────────────────────────────────────── */
function PageHeaderStrip({
    address,
    cinemaName,
    title,
    right,
}: {
    address?: string
    cinemaName: string
    title: string
    right?: React.ReactNode
}) {
    return (
        <div className="px-4 pt-2 pb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-4">
            <div className="min-w-0 shrink-0">
                {address && (
                    <p
                        className="mb-1 uppercase tracking-[0.12em] text-white/35 line-clamp-2 md:line-clamp-1"
                        style={{ fontFamily: MONO, fontWeight: 700, fontSize: 11 }}>
                        {address}
                    </p>
                )}
                <PageTitle titleKey={title}>{title}</PageTitle>
            </div>
            {right && <div className="w-full min-w-0 md:w-auto md:shrink-0">{right}</div>}
        </div>
    )
}

/* ─── Afisha page ─────────────────────────────────────────────────────────── */
export default function Afisha() {
    const { cinema } = appStore()
    const { request, requestData } = useRequest()
    const [searchParams, setSearchParams] = useSearchParams()

    const [dates, setDates] = useState<string[]>([])
    const [selectedDate, setSelectedDate] = useState<string>("")
    const [movies, setMovies] = useState<Movie[]>([])
    const [collections, setCollections] = useState<FilmCollectionGroup[]>([])
    const [banners, setBanners] = useState<AfishaBanner[]>([])
    const [actions, setActions] = useState<CinemaAction[]>([])
    const [actionsLoading, setActionsLoading] = useState(false)
    const [loading, setLoading] = useState(false)
    const [activeTab, setActiveTab] = useState<AfishaTab>(() => afishaTabFromSlug(searchParams.get("tab")) ?? "Афиша")
    const [calendarOpen, setCalendarOpen] = useState(false)
    const noSessionsModalRef = useRef<ConfirmModalHandle>(null)

    useEffect(() => {
        setActiveTab(afishaTabFromSlug(searchParams.get("tab")) ?? "Афиша")
    }, [searchParams])

    useEffect(() => {
        let cancelled = false

        const fetchBanners = async () => {
            const res = await requestData<AfishaBanner[]>({
                type: "GET",
                path: `cinema/${cinema.id}/banners?channel=site`,
                disableError: true,
            })

            if (cancelled) return
            setBanners(res.status === "success" ? normalizeAfishaBanners(res.data) : [])
        }

        fetchBanners()
        return () => {
            cancelled = true
        }
        // requestData намеренно не в deps — функция из useRequest не мемоизирована
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cinema.id])

    useEffect(() => {
        if (activeTab !== "Акции") return
        let cancelled = false

        const fetchActions = async () => {
            setActionsLoading(true)
            const res = await requestData<CinemaAction[]>({
                type: "GET",
                path: `cinema/${cinema.id}/actions?channel=site`,
                disableError: true,
            })
            if (cancelled) return
            setActions(res.status === "success" ? normalizeCinemaActions(res.data) : [])
            setActionsLoading(false)
        }

        fetchActions()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cinema.id, activeTab])

    // Fetch available dates when cinema changes
    useEffect(() => {
        const fetchDates = async () => {
            const data: string[] | null = await request("GET", `cinema/${cinema.id}/film/dates`)
            if (data?.length) {
                setDates(data)
                // Set initial date to today if available, otherwise first date
                const today = toISO(new Date())
                setSelectedDate(data.includes(today) ? today : data[0])
            }
        }
        fetchDates()
    }, [cinema.id])

    // Fetch movies / collections when cinema, date, or tab changes
    useEffect(() => {
        let cancelled = false

        const load = async () => {
            if (activeTab === "Подборки") {
                setLoading(true)
                try {
                    const data = await request<{ collections: FilmCollectionGroup[] }>(
                        "GET",
                        `cinema/${cinema.id}/film/collections`,
                    )
                    if (cancelled) return
                    setCollections(data?.collections ?? [])
                } finally {
                    if (!cancelled) setLoading(false)
                }
                return
            }

            if (!["Афиша", "Скоро"].includes(activeTab)) return
            if (activeTab !== "Скоро" && !selectedDate) return

            setLoading(true)
            const endpoint =
                activeTab === "Скоро"
                    ? `cinema/${cinema.id}/film/soon`
                    : `cinema/${cinema.id}/film?date=${selectedDate}`

            try {
                const data: Movie[] | null = await request("GET", endpoint)
                if (cancelled) return
                setMovies(data || [])
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        load()
        return () => {
            cancelled = true
        }
        // request намеренно не в deps — функция из useRequest не мемоизирована
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, selectedDate, cinema.id])

    const handleTabChange = (tab: AfishaTab) => {
        setActiveTab(tab)
        const slug = AFISHA_TAB_TO_SLUG[tab]
        if (slug === "afisha") {
            setSearchParams({}, { replace: true })
        } else {
            setSearchParams({ tab: slug }, { replace: true })
        }
    }

    const handleSoonSelect = () => {
        // «Скоро» остаётся в афише: меняется только список фильмов, без нового URL.
        setActiveTab("Скоро")
    }

    const handleDateSelect = (date: string) => {
        setSelectedDate(date)
        handleTabChange("Афиша")
    }

    const showDatePicker = (activeTab === "Афиша" || activeTab === "Скоро") && dates.length > 0

    return (
        <>
            <StartPopup cinemaId={cinema.id} />
            <ConfirmModal ref={noSessionsModalRef} />

            {/* Calendar modal */}
            <AnimatePresence>
                {calendarOpen && (
                    <CalendarModal
                        dates={dates}
                        selectedDate={selectedDate}
                        onSelect={d => {
                            setSelectedDate(d)
                            handleTabChange("Афиша")
                        }}
                        onClose={() => setCalendarOpen(false)}
                    />
                )}
            </AnimatePresence>

            <div className={banners.length > 0 ? "pt-8 pb-20 md:pb-4" : "pt-16 pb-20 md:pb-4"}>
                {banners.length > 0 && <AfishaBannerSlider banners={banners} />}

                {/* Page header strip */}
                <PageHeaderStrip cinemaName={cinema.name} title={activeTab === "Скоро" ? "Афиша" : activeTab} />

                {/* Date picker (only for Афиша tab) */}
                {showDatePicker && (
                    <div className="overflow-visible px-4 pt-3 pb-4">
                        <DatePicker
                            dates={dates}
                            selectedDate={selectedDate}
                            onSelect={handleDateSelect}
                            onUnavailableSelect={() =>
                                noSessionsModalRef.current?.open({
                                    title: "Сеансы скоро будут",
                                    description:
                                        "В данный момент сеансы на эту дату не загружены. Вы сможете приобрести билеты на этот день, как только они станут доступны.",
                                    actions: [{ label: "Понятно", variant: "primary", onClick: () => {} }],
                                })
                            }
                            onCalendarOpen={() => setCalendarOpen(true)}
                            onSoonSelect={handleSoonSelect}
                            soonActive={activeTab === "Скоро"}
                        />
                    </div>
                )}

                {/* Content */}
                <AnimatePresence mode="wait">
                    {(activeTab === "Афиша" || activeTab === "Скоро") ? (
                        loading ? (
                            <HomeLoader key="loader" />
                        ) : (
                            <motion.div
                                key={`${activeTab}-${selectedDate}`}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.2 }}
                                className={`flex flex-col gap-5 px-4 ${showDatePicker && activeTab === "Афиша" ? "pt-1" : "pt-2"}`}>
                                {movies.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center gap-4 py-24 text-white/30">
                                        <span className="text-[64px]">{activeTab === "Скоро" ? "🎬" : "📭"}</span>
                                        <p style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 20 }}>
                                            {activeTab === "Скоро" ? "Скоро в кино" : "Нет сеансов на эту дату"}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {movies.map((item, i) => (
                                            <MovieItem key={item.id} item={item} index={i} />
                                        ))}
                                        {movies.length <= 3 && <ScheduleSoonPlaceholder />}
                                    </>
                                )}
                            </motion.div>
                        )
                    ) : activeTab === "Подборки" ? (
                        loading ? (
                            <HomeLoader key="loader-coll" />
                        ) : (
                            <CollectionsTab key={`collections-${cinema.id}`} groups={collections} />
                        )
                    ) : activeTab === "Акции" ? (
                        <ActionsTab key={`actions-${cinema.id}`} actions={actions} loading={actionsLoading} />
                    ) : (
                        <EmptyTab key={activeTab} tab={activeTab} />
                    )}
                </AnimatePresence>
            </div>
        </>
    )
}
